import axios, { AxiosHeaders } from "axios";
import AttorneyHubAPIService from "_shared/_api/attorney-hub-api-service";
import Guid from "_shared/guids/guid";
import Session from "users/__session/session";
import FeeScheduleTemplate from "work/fee-schedule-templates/fee-schedule-template";
import FeeScheduleTemplateCommand from "./contracts/create-fee-schedule-template-api-request";
import UpdateFeeScheduleTemplateCommand from "./contracts/update-fee-schedule-template-command";
import { FeeScheduleTemplateJSON } from "work/fee-schedule-templates/_schemas/fee-schedule-template-schema";

export default class FeeScheduleTemplateCommandAPIService {
  private readonly authHeaders: AxiosHeaders = new AxiosHeaders();

  private authHeadersWithJson(): AxiosHeaders {
    return this.authHeaders.concat({ "Content-Type": "application/json-patch+json" });
  }

  private authHeadersWithPatch(): AxiosHeaders {
    return this.authHeaders.concat({ "Content-Type": "application/json-patch+json" });
  }

  constructor(session: Readonly<Session>) {
    if (!session.authToken?.value)
      throw new Error("Session must have an authToken to create a FeeScheduleTemplateAPIService");
    this.authHeaders.set("Authorization", `Bearer ${session.authToken.value}`);
  }

  async createFeeScheduleTemplate(
    template: FeeScheduleTemplate,
    creatorType: string = "client",
  ): Promise<FeeScheduleTemplateJSON> {
    const url = new URL(`/work/fee-schedule-templates`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.post(url.toString(), new FeeScheduleTemplateCommand(template, creatorType), {
        headers: this.authHeadersWithJson(),
      });
      return response.data;
    } catch (error) {
      throw new FeeScheduleTemplateAPIServiceError("createFeeScheduleTemplate", error);
    }
  }

  async updateFeeScheduleTemplate(
    originalTemplate: FeeScheduleTemplate,
    updatedTemplate: FeeScheduleTemplate,
  ): Promise<FeeScheduleTemplateJSON> {
    const url = new URL(`/work/fee-schedule-templates/${originalTemplate.id?.value}`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const payload = new UpdateFeeScheduleTemplateCommand(originalTemplate, updatedTemplate).payload;
      const response = await axios.patch(url.toString(), payload, {
        headers: this.authHeadersWithPatch(),
      });
      return response.data;
    } catch (error) {
      throw new FeeScheduleTemplateAPIServiceError("updateFeeScheduleTemplate", error);
    }
  }

  async deleteFeeScheduleTemplate(id: Guid): Promise<void> {
    const url = new URL(`/work/fee-schedule-templates/${id.value}`, AttorneyHubAPIService.apiBaseUrl);
    try {
      await axios.delete(url.toString(), {
        headers: this.authHeaders,
      });
    } catch (error) {
      throw new FeeScheduleTemplateAPIServiceError("deleteFeeScheduleTemplate", error);
    }
  }

  async archiveFeeScheduleTemplate(id: Guid): Promise<FeeScheduleTemplateJSON> {
    const url = new URL(`/work/fee-schedule-templates/${id.value}/archive`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.post(
        url.toString(),
        {},
        {
          headers: this.authHeaders,
        },
      );
      return response.data;
    } catch (error) {
      throw new FeeScheduleTemplateAPIServiceError("archiveFeeScheduleTemplate", error);
    }
  }

  async unarchiveFeeScheduleTemplate(id: Guid): Promise<FeeScheduleTemplateJSON> {
    const url = new URL(`/work/fee-schedule-templates/${id.value}/unarchive`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.post(
        url.toString(),
        {},
        {
          headers: this.authHeaders,
        },
      );
      return response.data;
    } catch (error) {
      throw new FeeScheduleTemplateAPIServiceError("unarchiveFeeScheduleTemplate", error);
    }
  }
}

export class FeeScheduleTemplateAPIServiceError extends Error {
  public method: string;
  public error: unknown;
  constructor(method: string, error: unknown) {
    super(
      `Error in FeeScheduleTemplateAPIService.${method}: ${error instanceof Error ? error.message : String(error)}`,
    );
    this.method = method;
    this.error = error;
  }
}
